/**
 * ActionBoxLayout — the drawing of an object's action box
 * (docs/features/order-to-invoice/27_action_box.md), shared by the order, the
 * estimate, the purchase order and the purchase purchase invoice. The box is what needs a
 * person; the chain under it is where things are.
 *
 * - Folded by default, it is one summary line, and the whole line is the
 *   button: "⚠ 4 to do · 1 late · 3 at risk · 4 waiting on Travelodge
 *   Show ▾" (several parties: "N waiting"). Open, the rows show under it
 *   and it says "Hide ▴". With anything to do the box takes the design
 *   system's warning tint; otherwise it is a neutral grey box (`bg-muted`):
 *   "Nothing to do · 2 waiting on Travelodge", or "✓ Everything
 *   is moving · Next: …" (nothing to show, no Show).
 * - A closed object (an accepted or declined estimate) shows its closing
 *   line instead, in the same grey box, and nothing to open.
 * - A to-do row is dot · verb · which thing · one reason or date · one
 *   button, in fixed columns so the buttons line up. The dot (red late,
 *   amber at risk) is the only signal: one text colour, reasons and dates
 *   muted. Waiting rows say who, what and when, with no button, under one
 *   heading per party; "auto" marks what the system did itself.
 *
 * Presentational: the rows' words come from the backend, mapped by the
 * caller. Below md a row wraps: verb and thing, then the reason, then the
 * button, right-aligned. A caller whose backend words the folded line
 * itself (the order's `actions.summary`) passes it as `summary`; a waiting
 * row may carry a dot too (the customer's rows, by the approve-by date); and
 * `footer` holds anything else the open box shows (planned rows).
 */
import { useId, type ReactNode } from "react";
import { Check, ChevronDown, TriangleAlert } from "lucide-react";
import { cn } from "../primitives/utils";

export type ActionBoxTier = "late" | "at_risk" | null;

export interface ActionBoxToDoRow {
  key: string;
  tier: ActionBoxTier;
  verb: string;
  thing: ReactNode;
  reason: ReactNode;
  button: ReactNode;
}

export interface ActionBoxWaitingRow {
  key: string;
  party: string;
  verb: string;
  thing: ReactNode;
  /** "Sent 1 Oct · next: reminder 1 Mon 5 Oct". */
  when: ReactNode;
  isAuto: boolean;
  /** Red late, amber at risk: only where the backend dots a waiting row. */
  tier?: ActionBoxTier;
}

/** The folded line as the backend words it: lead, parts, and whether anything is to do. */
export interface ActionBoxSummary {
  lead: string;
  parts: string[];
  tone: "to_do" | "waiting" | "clear";
}

/** Desktop: dot · verb · thing · reason · button, the button column one width. */
const ROW =
  "grid grid-cols-[0.75rem_minmax(0,1fr)] items-start gap-x-3 gap-y-1 border-t border-border py-2 first:border-t-0 group-data-[tone=warning]/box:border-warning/25 md:grid-cols-[0.75rem_9.5rem_minmax(0,1.25fr)_minmax(0,1fr)_11rem] md:items-center";

function Dot({ tier }: { tier: ActionBoxTier }) {
  return (
    <span
      className={cn(
        "mt-1.5 size-2 justify-self-center rounded-full md:mt-0",
        tier === "late" && "bg-red-600",
        tier === "at_risk" && "bg-amber-500",
      )}
      {...(tier
        ? { role: "img", "aria-label": tier === "late" ? "Late" : "At risk" }
        : { "aria-hidden": true })}
    />
  );
}

function ToDoRow({ row }: { row: ActionBoxToDoRow }) {
  return (
    <li className={ROW}>
      <Dot tier={row.tier} />
      {/* Below md: verb and thing on one line, the reason under them. */}
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 md:contents">
        <span className="font-semibold">{row.verb}</span>
        <span className="min-w-0">{row.thing}</span>
      </span>
      <span className="col-start-2 min-w-0 text-sm text-muted-foreground md:col-start-auto">
        {row.reason}
      </span>
      <span className="col-start-2 flex justify-end md:col-start-auto md:block">
        {row.button}
      </span>
    </li>
  );
}

function WaitingRow({ row }: { row: ActionBoxWaitingRow }) {
  return (
    <li className={ROW}>
      {row.tier ? <Dot tier={row.tier} /> : <span aria-hidden />}
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 md:contents">
        <span>{row.verb}</span>
        <span className="min-w-0">{row.thing}</span>
      </span>
      <span className="col-start-2 min-w-0 text-sm text-muted-foreground md:col-start-auto">
        {row.isAuto && (
          <span className="mr-1.5 whitespace-nowrap rounded-full border border-border px-1.5 text-xs">
            auto
          </span>
        )}
        {row.when}
      </span>
      <span aria-hidden className="max-md:hidden" />
    </li>
  );
}

/** What it waits on, a heading per party, in the given order within each. */
function WaitingSection({ rows }: { rows: ActionBoxWaitingRow[] }) {
  const parties = [...new Set(rows.map((r) => r.party))];
  return (
    <>
      {parties.map((party) => (
        <section
          key={party}
          aria-label={`Waiting on ${party}`}
          className="mt-1 border-t border-border pt-3 group-data-[tone=warning]/box:border-warning/25"
        >
          <h3 className="text-xs font-semibold text-muted-foreground">
            Waiting on {party}
          </h3>
          <ul>
            {rows
              .filter((r) => r.party === party)
              .map((row) => (
                <WaitingRow key={row.key} row={row} />
              ))}
          </ul>
        </section>
      ))}
    </>
  );
}

export function ActionBoxLayout({
  toDo,
  waiting,
  next,
  closed,
  isExpanded,
  onExpandedChange,
  summary: worded,
  footer,
}: {
  toDo: ActionBoxToDoRow[];
  waiting: ActionBoxWaitingRow[];
  /** With nothing to do or wait for: the next milestone. */
  next: string;
  /** A closed object's closing line, in place of the rows. */
  closed?: ReactNode;
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  /** The folded line, when the backend words it; otherwise counted here. */
  summary?: ActionBoxSummary;
  /** More rows the open box shows after the waiting ones (planned rows); they too open the box. */
  footer?: ReactNode;
}) {
  // Two boxes can share a page (the queue's panel beside a list), so the
  // heading and rows ids are per instance.
  const titleId = useId();
  const rowsId = useId();
  // A closing line with nothing left to do is the whole box; with to-dos
  // still open, they show and the closing line sits above them.
  if (closed && toDo.length === 0) {
    return (
      <section
        aria-label="Closed"
        data-tone="calm"
        className="rounded-lg border border-muted-foreground/20 bg-muted px-4 py-3 text-[15px]"
      >
        {closed}
      </section>
    );
  }
  const n = toDo.length;
  const w = waiting.length;
  const late = toDo.filter((r) => r.tier === "late").length;
  const atRisk = toDo.filter((r) => r.tier === "at_risk").length;
  const parties = [...new Set(waiting.map((r) => r.party))];
  const waitingOn =
    w === 0
      ? null
      : parties.length === 1
        ? `${w} waiting on ${parties[0]}`
        : `${w} waiting`;
  const canOpen = n > 0 || w > 0 || !!footer;
  const isOpen = canOpen && isExpanded;
  const lead =
    worded?.lead ??
    (n > 0 ? `${n} to do` : w > 0 ? "Nothing to do" : "Everything is moving");
  const parts =
    worded?.parts ??
    (n > 0
      ? [
          late > 0 && `${late} late`,
          atRisk > 0 && `${atRisk} at risk`,
          waitingOn,
        ]
      : w > 0
        ? [waitingOn]
        : [`Next: ${next}`]);
  const isClear = worded ? worded.tone === "clear" : n === 0 && w === 0;
  const summary = (
    <span className="flex min-w-0 items-center gap-2">
      {n > 0 ? (
        <TriangleAlert
          aria-hidden
          className="size-[18px] shrink-0 text-warning"
        />
      ) : (
        isClear && (
          <Check
            aria-hidden
            className="size-4 shrink-0 text-emerald-600"
            strokeWidth={3}
          />
        )
      )}
      <span className="flex flex-wrap items-baseline gap-x-1.5">
        <b className="font-semibold">{lead}</b>
        {parts.filter(Boolean).map((part) => (
          <span key={String(part)}>
            <span aria-hidden className="text-muted-foreground">
              ·{" "}
            </span>
            {part}
          </span>
        ))}
      </span>
    </span>
  );
  return (
    <section
      aria-labelledby={titleId}
      data-tone={n > 0 ? "warning" : "calm"}
      className={cn(
        "group/box rounded-lg border px-4",
        n > 0
          ? "border-warning/40 bg-warning/7"
          : "border-muted-foreground/20 bg-muted",
      )}
    >
      {/* Folded by default: the summary line is the box, and its button. */}
      <h2 id={titleId} className="text-[15px] font-normal">
        {canOpen ? (
          <button
            type="button"
            aria-expanded={isOpen}
            aria-controls={rowsId}
            onClick={() => onExpandedChange(!isExpanded)}
            className="flex w-full items-center justify-between gap-3 rounded-sm py-3 text-left text-[15px] font-normal outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {summary}{" "}
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold">
              {isOpen ? "Hide" : "Show"}
              <ChevronDown
                aria-hidden
                className={cn(
                  "size-4 transition-transform",
                  isOpen && "rotate-180",
                )}
              />
            </span>
          </button>
        ) : (
          <span className="flex py-3">{summary}</span>
        )}
      </h2>
      {isOpen && (
        <div id={rowsId} className="pb-2.5">
          {closed && (
            <div
              aria-label="Closed"
              role="region"
              className="border-t border-border py-2 text-sm group-data-[tone=warning]/box:border-warning/25"
            >
              {closed}
            </div>
          )}
          {n > 0 && (
            <ul
              aria-label="To do"
              className="border-t border-border group-data-[tone=warning]/box:border-warning/25"
            >
              {toDo.map((row) => (
                <ToDoRow key={row.key} row={row} />
              ))}
            </ul>
          )}
          {w > 0 && <WaitingSection rows={waiting} />}
          {footer && (
            <div className="mt-1 border-t border-border pt-3 group-data-[tone=warning]/box:border-warning/25">
              {footer}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
